import React, { useContext, useEffect, useState } from "react"
import AvailabilityCards from "./AvailabilityCards"
import DeleteAvailabilityModal from "./DeleteAvailabilityModal"
import InfoModal from "../../InfoModal"
import useLoading from "../../../hooks/useLoading"
import { AvailabilityRulesData } from "../../../types/availabilityTypes"
import { Button } from "../../ui/button"
import { CalendarRequests, ServiceRequests } from "../../../api/app.service"
import { defaultAvailability } from "../../../objects/availabilityObjects"
import { SvgFilledInformationCircle, SvgPlus } from "../../icons"
import { UserCtx } from "../../../context/userContext"
import { useNavigate } from "react-router-dom"
import { useToast } from "../../../hooks/useToast"

interface Props {
  refetchTrigger: boolean
}

const AvailabilityHours: React.FC<Props> = ({ refetchTrigger }) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [availabilities, setAvailabilities] = useState<
    Array<AvailabilityRulesData>
  >([])
  const [selectedSchedule, setSelectedSchedule] =
    useState<AvailabilityRulesData | null>(null)
  const [isUnsavedCalendar, setIsUnsavedCalendar] = useState<boolean>(false)
  const [availabilityDetails, setAvailabilityDetails] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showOnOffModal, setShowOnOffModal] = useState(false)
  const [services, setServices] = useState<any[]>([])
  const { stopLoading } = useLoading()

  const [availabilityToDelete, setAvailabilityToDelete] =
    useState<AvailabilityRulesData | null>(null)

  const getAvailabilityRules = () => {
    if (user?.coachProfile) {
      setLoading(true)
      CalendarRequests.getAvailabilityRules({
        user_id: parseInt(user.user_id),
      })
        .then((data) => {
          if (data.availability_rules.length > 0) {
            const normalized = data.availability_rules.map((a: any) => ({
              ...a,
              is_default:
                a.is_default === "1" ||
                a.is_default === 1 ||
                a.is_default === true,
            }))

            setAvailabilities(normalized)

            const defaultSchedule = normalized.find((a) => a.is_default)
            if (defaultSchedule) {
              setSelectedSchedule(defaultSchedule)
            } else {
              setSelectedSchedule(normalized[0])
            }
          } else {
            setIsUnsavedCalendar(true)
            setSelectedSchedule(defaultAvailability)
          }
          setLoading(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          setLoading(false)
        })
    }
  }

  const handleDeleteClick = (availability: AvailabilityRulesData) => {
    setAvailabilityToDelete(availability)
    setShowDeleteModal(true)
  }

  const deleteAvailabilityRule = () => {
    if (!availabilityToDelete?.id) {
      toast.error("You must save a newly created calendar before deleting it.")
      return
    }

    if (availabilityToDelete?.is_default) {
      toast.error("Can't delete the default availability rule.")
      return
    }

    setLoading(true)

    CalendarRequests.deleteAvailabilityRule({
      user_id: parseInt(user.user_id),
      availability_rule_id: availabilityToDelete.id,
    })
      .then(() => {
        toast.success("Availability deleted")
        setAvailabilities((prev) =>
          prev.filter((a) => a.id !== availabilityToDelete.id)
        )
        setShowDeleteModal(false)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
      .finally(() => {
        getServices()
        setLoading(false)
      })
  }

  const getServices = () => {
    if (user) {
      ServiceRequests.getServiceListClient({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
        .then((data) => {
          setServices(data.service_list)
          setLoading(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          setLoading(false)
        })
        .finally(() => {
          stopLoading()
        })
    }
  }

  const addNewAvailability = () => {
    navigate(`/account-settings/availability`, {})
  }

  useEffect(() => {
    getAvailabilityRules()
    getServices()
  }, [])

  useEffect(() => {
    getAvailabilityRules()
  }, [refetchTrigger])

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col px-4 md:pt-0 bg-white rounded-xl main-shadow">
      <div className="flex flex-col p-8 mt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full">
          <div className="flex items-center space-x-2 pr-2">
            <p className="font-bold text-xl">Availability Hours</p>
            <Button
              onClick={() => setAvailabilityDetails(true)}
              variant="ghost"
              size="icon"
            >
              <SvgFilledInformationCircle className="w-4 h-4" />
            </Button>
          </div>

          <Button
            variant="default"
            className="mt-2 md:mt-0 md:ml-auto w-max flex items-center space-x-1"
            onClick={addNewAvailability}
          >
            <SvgPlus className="w-4 h-4" />
            <span>Add Availability</span>
          </Button>
        </div>
        <div className="bg-grayMist w-full h-[1px] mt-2"></div>
      </div>

      <div className="flex justify-center w-full mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-4 max-w-fit">
          {availabilities?.map((availability) => (
            <div key={availability.id} className="w-full max-w-md">
              <AvailabilityCards
                services={services}
                availability={availability}
                onDeleteClick={handleDeleteClick}
                setShowOnOffModal={setShowOnOffModal}
                user={user}
                getAvailabilityRules={getAvailabilityRules}
              />
            </div>
          ))}
        </div>
      </div>

      {availabilityDetails && (
        <InfoModal
          title="Smart Availability Hours"
          description={
            <div className="">
              <p>
                <b>Create your perfect work schedule.</b> Define when you're at
                your best and let Zoee automatically guide clients to those
                ideal booking times.
              </p>
              <p className="mt-6">How it works:</p>
              <ul className="list-disc ml-5 mb-6">
                <li>
                  <b>Set your perfect schedule</b> — choose exactly when you're
                  available for sessions
                </li>
                <li>
                  <b>Automatic client guidance</b> — only your available hours
                  are shown to clients
                </li>
                <li>
                  <b>Flexible updates</b> — easily adjust your schedule as your
                  routine evolves
                </li>
              </ul>
              <p>
                Create boundaries that work for you. Your Availability Hours
                ensure clients book at times that fit your lifestyle, giving you
                a predictable schedule and eliminating those awkward "Can we
                meet at 6 AM?" requests.
              </p>
              <p className="italic mt-4">
                Professional scheduling that respects your personal rhythm.
              </p>
            </div>
          }
          setShowModal={setAvailabilityDetails}
          textAlign="left"
        />
      )}

      {showDeleteModal && availabilityToDelete && (
        <DeleteAvailabilityModal
          availability={availabilityToDelete}
          setShowDeleteDialogueModal={setShowDeleteModal}
          deleteAvailabilityRule={deleteAvailabilityRule}
          loading={loading}
        />
      )}

      {showOnOffModal && (
        <InfoModal
          title="Availability Status: OFF"
          description="When this is set to OFF, your clients will not be able to book any services connected to this availability. This setting gives you more control over your schedule, without fully disabling your services."
          setShowModal={setShowOnOffModal}
        />
      )}
    </div>
  )
}

export default AvailabilityHours
